/**
 * Tiny native-Unicode emoji picker for the blog editor.
 *
 * Deliberately built from scratch (no `emoji-mart`, `emoji-picker-react`
 * etc.) so we don't add ~250-600KB to the admin bundle for a feature
 * that only needs to insert characters at the cursor. The curated set
 * below covers ~200 of the most common emojis split into 8 tabs.
 */

const EMOJI_GROUPS = [
  {
    id: "smileys",
    label: "Smileys",
    icon: "😀",
    items: [
      "😀", "😃", "😄", "😁", "😆", "😂", "🤣", "😅", "😊", "😇",
      "🙂", "🙃", "😉", "😌", "😍", "🥰", "😘", "😗", "😙", "😚",
      "😋", "😛", "😜", "🤪", "😝", "🤑", "🤗", "🤭", "🤫", "🤔",
      "🤐", "🤨", "😐", "😑", "😶", "😏", "😒", "🙄", "😬", "😮‍💨",
      "🤥", "😌", "😔", "😪", "🤤", "😴", "😷", "🤒", "🤕", "🤢",
      "🥵", "🥶", "😵", "🤯", "🤠", "🥳", "😎", "🤓", "🧐", "😕",
      "😟", "🙁", "☹️", "😮", "😯", "😲", "😳", "🥺", "😦", "😧",
      "😨", "😰", "😥", "😢", "😭", "😱", "😖", "😣", "😞", "😓",
      "😩", "😫", "😤", "😡", "😠", "🤬", "😈", "👿", "💀", "💩",
    ],
  },
  {
    id: "gestures",
    label: "Gestures",
    icon: "👍",
    items: [
      "👋", "🤚", "🖐️", "✋", "🖖", "👌", "🤌", "🤏", "✌️", "🤞",
      "🤟", "🤘", "🤙", "👈", "👉", "👆", "🖕", "👇", "☝️", "👍",
      "👎", "✊", "👊", "🤛", "🤜", "👏", "🙌", "👐", "🤲", "🤝",
      "🙏", "💪", "🦾", "🦵", "🦶", "👂", "🦻", "👃", "🧠", "👀",
      "👁️", "👅", "👄", "🦷",
    ],
  },
  {
    id: "people",
    label: "People",
    icon: "🙋",
    items: [
      "👶", "🧒", "👦", "👧", "🧑", "👱", "👨", "🧔", "👩", "🧓",
      "👴", "👵", "🙍", "🙎", "🙅", "🙆", "💁", "🙋", "🧏", "🙇",
      "🤦", "🤷", "👮", "🕵️", "💂", "👷", "🤴", "👸", "👳", "👲",
      "🤵", "👰", "🤰", "🤱", "🧑‍🎓", "👨‍🎓", "👩‍🎓", "🧑‍🏫", "🧑‍🌾", "🧑‍🍳",
      "🧑‍🔧", "🧑‍🏭", "🧑‍💼", "🧑‍🔬", "🧑‍💻", "🧑‍🎤", "🧑‍🎨", "🧑‍🚒", "🧑‍✈️", "🧑‍🚀",
    ],
  },
  {
    id: "nature",
    label: "Nature",
    icon: "🌳",
    items: [
      "🐶", "🐱", "🐭", "🐹", "🐰", "🦊", "🐻", "🐼", "🐨", "🐯",
      "🦁", "🐮", "🐷", "🐸", "🐵", "🐔", "🐧", "🐦", "🐤", "🦆",
      "🦅", "🦉", "🐺", "🐗", "🐴", "🦄", "🐝", "🐛", "🦋", "🐌",
      "🐞", "🐜", "🦗", "🕷️", "🐢", "🐍", "🦖", "🦕", "🐙", "🦑",
      "🐠", "🐟", "🐡", "🦈", "🐳", "🐋", "🌲", "🌳", "🌴", "🌵",
      "🌾", "🌿", "🍀", "🍁", "🍂", "🍃", "🌷", "🌹", "🌻", "🌼",
      "🌞", "🌝", "🌚", "🌍", "🌎", "🌏", "⭐", "🌟", "✨", "⚡",
      "☀️", "🌤️", "⛅", "☁️", "🌧️", "⛈️", "❄️", "☃️", "🌈", "🔥",
    ],
  },
  {
    id: "food",
    label: "Food",
    icon: "🍔",
    items: [
      "🍏", "🍎", "🍐", "🍊", "🍋", "🍌", "🍉", "🍇", "🍓", "🫐",
      "🍈", "🍒", "🍑", "🥭", "🍍", "🥥", "🥝", "🍅", "🍆", "🥑",
      "🥦", "🥬", "🥒", "🌶️", "🌽", "🥕", "🥔", "🍠", "🥐", "🥯",
      "🍞", "🥖", "🥨", "🧀", "🥚", "🍳", "🧈", "🥞", "🧇", "🥓",
      "🍔", "🍟", "🍕", "🌭", "🥪", "🌮", "🌯", "🥙", "🍖", "🍗",
      "🍝", "🍜", "🍲", "🍣", "🍱", "🍛", "🍙", "🍚", "🍘", "🍢",
      "🍡", "🍧", "🍨", "🍦", "🥧", "🍰", "🎂", "🍮", "🍭", "🍬",
      "🍫", "🍩", "🍪", "🌰", "🥜", "🍯", "🥛", "🍼", "☕", "🍵",
      "🍺", "🍻", "🥂", "🍷", "🥃", "🍸", "🍹", "🍾", "🥤", "🧃",
    ],
  },
  {
    id: "activity",
    label: "Activity",
    icon: "⚽",
    items: [
      "⚽", "🏀", "🏈", "⚾", "🎾", "🏐", "🏉", "🥏", "🎱", "🪀",
      "🏓", "🏸", "🥊", "🥋", "🎽", "🛹", "⛸️", "🥌", "🎿", "⛷️",
      "🏂", "🪂", "🏋️", "🤼", "🤸", "🤺", "🤾", "🏌️", "🏇", "🧘",
      "🏆", "🥇", "🥈", "🥉", "🎖️", "🏅", "🎗️", "🎫", "🎟️", "🎪",
      "🎤", "🎧", "🎼", "🎵", "🎶", "🎹", "🥁", "🎷", "🎺", "🎸",
      "🎻", "🎲", "🧩", "🎯", "🎳", "🎮", "🎰", "🃏", "🎴", "🀄",
    ],
  },
  {
    id: "travel",
    label: "Travel",
    icon: "🚗",
    items: [
      "🚗", "🚕", "🚙", "🚌", "🚎", "🏎️", "🚓", "🚑", "🚒", "🚐",
      "🛻", "🚚", "🚛", "🚜", "🦯", "🦽", "🦼", "🛴", "🚲", "🛵",
      "🏍️", "🛺", "🚨", "🚔", "🚍", "🚘", "🚖", "🚡", "🚠", "🚟",
      "🚃", "🚋", "🚞", "🚝", "🚄", "🚅", "🚈", "🚂", "🚆", "🚇",
      "✈️", "🛫", "🛬", "🛩️", "💺", "🛰️", "🚀", "🛸", "🚁", "⛵",
      "🚤", "🛥️", "🛳️", "⛴️", "🚢", "⚓", "⛽", "🚧", "🚦", "🚥",
      "🗺️", "🗿", "🗽", "🗼", "🏰", "🏯", "🏟️", "🎡", "🎢", "🎠",
    ],
  },
  {
    id: "objects",
    label: "Objects",
    icon: "💡",
    items: [
      "💡", "🔦", "🕯️", "🪔", "🧯", "🛢️", "💸", "💵", "💴", "💶",
      "💷", "🪙", "💰", "💳", "💎", "⚖️", "🪜", "🧰", "🪛", "🔧",
      "🔨", "🛠️", "⛏️", "🪚", "🔩", "⚙️", "🪤", "🧱", "🔗", "📌",
      "📍", "📎", "✂️", "🖊️", "🖋️", "✒️", "🖌️", "🖍️", "📝", "✏️",
      "📚", "📖", "📒", "📔", "📕", "📗", "📘", "📙", "📰", "🗞️",
      "📱", "📲", "💻", "⌨️", "🖥️", "🖨️", "🖱️", "🖲️", "🕹️", "💾",
      "💿", "📀", "📟", "📞", "☎️", "📠", "📺", "📻", "🎙️", "🎚️",
      "📷", "📸", "📹", "🎥", "📽️", "🎬",
    ],
  },
  {
    id: "symbols",
    label: "Symbols",
    icon: "❤️",
    items: [
      "▲_green", "▲", "▼", "◄►", "↔️_orange", "↔️", "🆕_blue", "🆕", "📈", "📉", "🔺", "🔻", "🟢", "🔴", "🟠", "⭐", "✨",
      "❤️", "🧡", "💛", "💚", "💙", "💜", "🖤", "🤍", "🤎", "💔",
      "❣️", "💕", "💞", "💓", "💗", "💖", "💘", "💝", "💟", "☮️",
      "✝️", "☪️", "🕉️", "☸️", "✡️", "🔯", "🕎", "☯️", "☦️", "🛐",
      "⛎", "♈", "♉", "♊", "♋", "♌", "♍", "♎", "♏", "♐",
      "♑", "♒", "♓", "🆔", "⚛️", "🉑", "☢️", "☣️", "📴", "📳",
      "✅", "✔️", "☑️", "❌", "❎", "➕", "➖", "➗", "✖️", "♾️",
      "‼️", "⁉️", "❓", "❔", "❕", "❗", "〰️", "💱", "💲", "⚕️",
      "♻️", "⚜️", "🔱", "📛", "🔰", "⭕", "🔴", "🟠", "🟡", "🟢",
      "🔵", "🟣", "⚫", "⚪", "🟤", "🔶", "🔷", "🔸", "🔹", "🔺",
      "🔻", "💠", "🔘", "🔳", "🔲",
    ],
  },
];

export default EMOJI_GROUPS;
